/**
 * Centralized Homura & Kyubey Card Parser & Universal Data Adapter
 * 
 * Centraliza o processamento e a interpretação de qualquer JSON de carta
 * recebido das APIs externas (Homura e Kyubey proxy), transformando-o em um
 * modelo universal, robusto, prático e padronizado para toda a aplicação.
 */

import { parseCardRarity } from '../components/CardDetailModal';
import { findGame, getGameSlug } from '../constants/games';
import { setPriceInCache } from './cardPriceService';

export interface UniversalVariant {
  id?: string | number;
  unique_id?: string;
  tcgplayerId?: string | number | null;
  tcgplayer_id?: string | number | null;
  cardmarketId?: string | number | null;
  code?: string | null;
  variant_code?: string | null;
  variantCode?: string | null;
  variant_external_id?: string | null;
  variant_number?: number;
  collector_number?: string | number | null;
  print_number?: string | number | null;
  name?: string | null;
  justname?: string | null;
  printing?: string | null;
  finish?: string | null;
  foiling?: string | null;
  edition?: string | null;
  condition?: string | null;
  language?: string | null;
  rarity?: string | null;
  set_rarity?: string | null;
  rarity_code?: string | null;
  set_code?: string | null;
  setCode?: string | null;
  set_name?: string | null;
  setName?: string | null;
  groupId?: number | null;
  release_date?: string | null;
  releaseDate?: string | null;
  image_url?: string | null;
  imageUrl?: string | null;
  images?: any;
  price: number; // Em BRL
  raw_price?: number; // Preço original em USD
  marketPrice?: number | null;
  lowPrice?: number | null;
  midPrice?: number | null;
  highPrice?: number | null;
  avgPrice?: number | null;
  raw?: any;
}

export interface UniversalCard {
  // Identificadores universais
  id: string;
  external_id: string;
  code: string;
  name: string;
  cleanName: string;
  subname?: string;
  display_name?: string;

  // Franquia / Jogo
  game: string;
  game_id: number | string;
  game_slug?: string;

  // Coleção / Set
  set: string;
  set_name: string;
  set_code: string;
  setCode: string;
  setName: string;
  groupId?: number | null;
  set_release_date?: string | null;

  // Raridade
  rarity: string;
  set_rarity?: string;
  raw_rarity?: any;
  is_foil?: boolean;

  // Imagens
  imageUrl: string;
  image_url: string;
  images?: {
    small?: string;
    medium?: string;
    large?: string;
    original?: string;
    bandai?: string;
    [key: string]: any;
  };
  cardBackUrl?: string;
  card_back_url?: string;

  // Tipos & Categorias
  type: string;
  cardType: string;
  card_type: string;
  category: string;
  stage?: string;
  super?: string;
  supertype?: string;
  types: string[];
  subtypes: string[];
  classes: string[];
  class?: string;
  frameType?: string;
  typeline?: string[];
  classifications?: string | string[];

  // Cores, Fações & Elementos
  color: string;
  colors: string[];
  pokemonType?: string;
  attribute?: string;
  faction?: string;
  faction_data?: { code?: string; name?: string; color?: string };
  element?: string;
  elements?: any;
  nation?: string;
  clan?: string;
  aspects?: string[];
  arenas?: string[];
  domain?: string;
  family?: string;
  trait?: string;
  traits: string[];
  tags: string[];
  race?: string;
  archetype?: string;
  icons?: string[];

  // Atributos numéricos e de combate
  cost?: string | number | null;
  cost_memory?: number | null;
  cost_reserve?: number | null;
  playCost?: string | number | null;
  energyCost?: string | number | null;
  cmc?: number | null;
  manaCost?: string | null;
  power?: string | number | null;
  attack?: string | number | null;
  defense?: string | number | null;
  def?: string | number | null;
  toughness?: string | number | null;
  might?: string | number | null;
  hp?: string | number | null;
  life?: string | number | null;
  ap?: string | number | null;
  bp?: string | number | null;
  dp?: string | number | null;
  ram?: string | number | null;
  level?: string | number | null;
  grade?: string | number | null;
  counter?: string | number | null;
  counterPower?: string | number | null;
  comboPower?: string | number | null;
  threat?: string | number | null;
  shield?: string | number | null;
  vitality?: string | number | null;
  hand_size?: string | number | null;
  difficulty?: string | number | null;
  control?: string | number | null;
  block?: string | number | null;
  block_zone?: string | null;
  pitch?: string | number | null;
  arcane?: string | number | null;
  lore?: string | number | null;
  inkable?: boolean | null;
  strength?: string | number | null;
  willpower?: string | number | null;
  bloomLevel?: string | null;
  retreat?: number | null;
  weaknesses?: string | null;
  weakness?: string | null;
  resistances?: string | null;
  resistance?: string | null;

  // Efeitos, Habilidades, Triggers & Texto
  effect?: string;
  securityEffect?: string | null;
  description: string;
  text?: any;
  ability?: string;
  rules_text?: string;
  trigger?: string | null;
  affinity?: string | null;
  characterTraits?: string | null;
  batonPass?: any;
  arts?: any;
  oshiSkill?: any;
  spOshiSkill?: any;
  cd2?: any;
  cd3?: any;
  guardian?: any;
  flavor?: string | null;
  flavor_text?: string | null;
  illustrator?: string;
  artist?: string;

  // Cotação e Valores
  price: number; // Preço calculado em R$
  raw_price: number; // Menor cotação bruta encontrada
  marketPrice?: number | null;
  lowPrice?: number | null;
  midPrice?: number | null;
  highPrice?: number | null;
  tcgplayerId?: string | number | null;
  tcgplayer_id?: string | null;

  // Variantes
  variants: UniversalVariant[];
  variant_number: number;
  variant_code: string | null;
  variant_external_id: string | null;

  // Payload bruto original preservado
  raw?: any;
  [key: string]: any;
}

export interface ParseCardOptions {
  game?: string | number;
  gameId?: string | number;
  gameName?: string;
  cardBackUrl?: string;
  fallbackSetCode?: string;
  fallbackSetName?: string;
}

/**
 * Taxa de conversão USD -> BRL padrão para cartas
 */
export const USD_TO_BRL_RATE = 5.0;

/**
 * Helper to reliably clean and extract a valid set_code (never returns a variant code like 'RDO-252' or 'OP01-001')
 */
export const cleanSetCodeString = (val: any): string | null => {
  if (!val) return null;
  let str = '';
  if (typeof val === 'string') {
    str = val.trim();
  } else if (typeof val === 'object') {
    str = String(val.set_code || val.setCode || val.code || val.prefix || '').trim();
  } else {
    str = String(val).trim();
  }
  if (!str || str === 'null' || str === 'undefined') return null;

  // 1. Bracket format like '[OP09]' or '[ST-16]' or '[EB01]' or '[OP15-EB04]'
  const bracketMatch = str.match(/\[([A-Za-z0-9-]+)\]/);
  if (bracketMatch && bracketMatch[1]) {
    const inside = bracketMatch[1].trim();
    if (/^[A-Za-z]+-\d+$/i.test(inside)) {
      return inside.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    }
    if (inside.includes('-')) {
      const parts = inside.split('-');
      if (parts[0] && parts[0].length <= 10) return parts[0].replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    }
    const cleaned = inside.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (cleaned.length > 0 && cleaned.length <= 10) return cleaned;
  }

  // 2. Colon format like 'SV06: Twilight Masquerade' -> 'SV06'
  if (str.includes(':')) {
    const prefix = str.split(':')[0].trim();
    if (prefix && prefix.length <= 10 && !prefix.includes(' ')) {
      return prefix.toUpperCase();
    }
  }

  // 3. Underscore format like 'BT24-004_BT24' or 'BT9-047_AD01' or 'BT18-060_BT1819'
  if (str.includes('_')) {
    const parts = str.split('_');
    if (parts[1] && parts[1].length <= 10 && !parts[1].includes('/') && /^[A-Za-z0-9-]+$/.test(parts[1])) {
      const cleaned = cleanSetCodeString(parts[1]);
      if (cleaned) return cleaned;
    }
    const cleaned0 = cleanSetCodeString(parts[0]);
    if (cleaned0) return cleaned0;
  }

  // 4. Variant code format with hyphen like 'RDO-252', 'OP01-001', 'FB06-056', 'EX12-047', 'DOA Alter-081', 'CH02-EN001'
  if (str.includes('-')) {
    const parts = str.split('-');
    const lastPart = parts[parts.length - 1].trim();
    if (/^\d+$/i.test(lastPart) || /^[A-Za-z]{0,3}\d+$/i.test(lastPart) || /^\d+[A-Za-z]?$/i.test(lastPart)) {
      const prefix = parts.slice(0, parts.length - 1).join('-').trim();
      if (prefix && prefix.length <= 12) {
        return prefix.toUpperCase();
      }
    }
  }

  // 4. Slash format like '091/217' or '128/167' -> not set code
  if (str.includes('/')) {
    return null;
  }

  // 5. Short string without spaces or standard set codes like 'DOA Alter', 'HVN 1st', 'RDO 1st'
  if (str.length <= 10) {
    return str.toUpperCase();
  }

  return null;
};

/**
 * Normaliza e limpa textos gerais
 */
export function cleanCardText(val: any): string {
  if (!val) return '';
  if (typeof val === 'string') {
    return val
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<strong>/gi, '')
      .replace(/<\/strong>/gi, '')
      .replace(/<em>/gi, '')
      .replace(/<\/em>/gi, '')
      .replace(/\r\n/g, '\n')
      .trim();
  }
  if (Array.isArray(val)) {
    return val.map(cleanCardText).filter(Boolean).join('\n\n');
  }
  if (typeof val === 'object') {
    const parts: string[] = [];
    if (val.card) parts.push(cleanCardText(val.card));
    if (val.attack_1) parts.push(cleanCardText(val.attack_1));
    if (val.attack_2) parts.push(cleanCardText(val.attack_2));
    if (val.raw_text) parts.push(cleanCardText(val.raw_text));
    if (val.rulesText) parts.push(cleanCardText(val.rulesText));
    if (val.MAIN_EFFECT) parts.push(cleanCardText(val.MAIN_EFFECT));
    if (val.effect) parts.push(cleanCardText(val.effect));
    if (val.ability) parts.push(cleanCardText(val.ability));
    if (val.text) parts.push(cleanCardText(val.text));
    return parts.filter(Boolean).join('\n\n');
  }
  return String(val);
}

/**
 * Verifica se a URL da imagem é válida e acessível (excluindo distribuições restritas conhecidas como 403)
 */
export function isUsableImageUrl(url: any): boolean {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) return false;
  // A distribuição dstcynss47vun.cloudfront.net retorna 403 Forbidden para todas as cartas de Cyberpunk
  if (trimmed.includes('dstcynss47vun.cloudfront.net')) return false;
  return true;
}

/**
 * Extrai a melhor URL de imagem da carta
 */
export function extractCardImage(c: any): string | undefined {
  if (!c) return undefined;

  // 1. Strings diretas
  const directUrlCandidates = [
    c.imageUrl,
    c.image_url,
    c.source_image_url,
    c.source_data?.image_url,
    typeof c.image === 'string' ? c.image : null
  ];
  for (const url of directUrlCandidates) {
    if (isUsableImageUrl(url)) {
      return url.trim();
    }
  }

  // 2. Objeto images (small, medium, large, original, bandai, front)
  if (c.images && typeof c.images === 'object') {
    if (Array.isArray(c.images) && c.images.length > 0) {
      const first = c.images[0];
      if (isUsableImageUrl(first)) return first;
      if (first && typeof first === 'object') {
        const found = first.large || first.original || first.medium || first.small || first.image_url || first.url;
        if (isUsableImageUrl(found)) return found;
      }
    } else {
      const found = c.images.large ||
                    c.images.original ||
                    c.images.medium ||
                    c.images.front ||
                    c.images.bandai ||
                    c.images.small ||
                    c.images.image_url ||
                    c.images.url;
      if (isUsableImageUrl(found)) {
        return found;
      }
    }
  }

  // 3. Imagens de variantes
  if (Array.isArray(c.variants) && c.variants.length > 0) {
    for (const v of c.variants) {
      if (!v) continue;
      const vDirect = v.image_url || v.imageUrl || v.source_image_url;
      if (isUsableImageUrl(vDirect)) {
        return vDirect;
      }
      if (v.images && typeof v.images === 'object') {
        const vFound = v.images.large || v.images.original || v.images.medium || v.images.small || v.images.image_url;
        if (isUsableImageUrl(vFound)) {
          return vFound;
        }
      }
      // TCGPlayer ID CDN fallback
      const tcgId = v.tcgplayerId ?? v.tcgplayer_id ?? v.tcgPlayerId;
      if (tcgId && !isNaN(Number(tcgId)) && Number(tcgId) > 0) {
        return `https://tcgplayer-cdn.tcgplayer.com/product/${tcgId}_in_1000x1000.jpg`;
      }
    }
  }

  // 4. Edições (FaB/editions)
  if (Array.isArray(c.editions) && c.editions.length > 0) {
    const firstEd = c.editions[0];
    const edUrl = firstEd?.image_url || firstEd?.imageUrl || firstEd?.image;
    if (typeof edUrl === 'string' && edUrl.startsWith('http')) return edUrl;
  }

  // 5. TCGPlayer ID direto na raiz
  const rootTcgId = c.tcgplayerId ?? c.tcgplayer_id ?? c.tcgPlayerId;
  if (rootTcgId && !isNaN(Number(rootTcgId)) && Number(rootTcgId) > 0) {
    return `https://tcgplayer-cdn.tcgplayer.com/product/${rootTcgId}_in_1000x1000.jpg`;
  }

  return undefined;
}

/**
 * Localiza a variante em c.variants que melhor corresponde ao código ou nome da coleção buscada
 */
export function findMatchingVariant(c: any, targetCode?: string, targetName?: string): any | null {
  if (!c || !Array.isArray(c.variants) || c.variants.length === 0) return null;
  const tCode = (targetCode || '').toLowerCase().trim();
  const tName = (targetName || '').toLowerCase().trim();

  if (tCode || tName) {
    const found = c.variants.find((v: any) => {
      if (!v) return false;
      const vSet = typeof v.set === 'object' && v.set ? v.set : null;
      const vCode = String(v.set_code || v.setCode || vSet?.set_code || vSet?.code || '').toLowerCase().trim();
      const vName = String(v.set_name || v.setName || vSet?.name || vSet?.set_name || vSet?.beauty_name || (typeof v.set === 'string' ? v.set : '') || '').toLowerCase().trim();
      const vVarCode = String(v.variant_code || v.variantCode || v.code || '').toLowerCase().trim();

      if (tCode) {
        if (vCode === tCode || vCode.replace(/[-_]/g, '') === tCode.replace(/[-_]/g, '')) return true;
        if (vVarCode && (vVarCode.startsWith(tCode + '-') || vVarCode.startsWith(tCode + '_') || vVarCode.includes(tCode))) return true;
      }
      if (tName) {
        if (vName === tName || vName.includes(tName) || tName.includes(vName)) return true;
      }
      return false;
    });
    if (found) return found;
  }

  // Fallback: primeira variante que possui informações de set
  const withSet = c.variants.find((v: any) => v && (v.set || v.set_code || v.setCode));
  return withSet || c.variants[0] || null;
}

/**
 * Verifica se a carta pertence a um set/coleção específico
 */
export function cardBelongsToSet(c: any, targetCode?: string, targetName?: string): boolean {
  if (!c) return false;
  const tCode = (targetCode || '').toLowerCase().trim();
  const tName = (targetName || '').toLowerCase().trim();
  if (!tCode && !tName) return true;

  const rootSet = typeof c.set === 'object' && c.set ? c.set : null;
  const rootCode = String(c.set_code || c.setCode || rootSet?.set_code || rootSet?.code || (typeof c.set === 'string' && c.set.length <= 15 ? c.set : '') || '').toLowerCase().trim();
  const rootName = String(c.set_name || c.setName || rootSet?.name || rootSet?.set_name || (typeof c.set === 'string' ? c.set : '') || '').toLowerCase().trim();

  if (tCode && rootCode && (rootCode === tCode || rootCode.replace(/[-_]/g, '') === tCode.replace(/[-_]/g, ''))) return true;
  if (tName && rootName && (rootName === tName || rootName.includes(tName) || tName.includes(rootName))) return true;

  if (Array.isArray(c.variants)) {
    return c.variants.some((v: any) => {
      if (!v) return false;
      const vSet = typeof v.set === 'object' && v.set ? v.set : null;
      const vCode = String(v.set_code || v.setCode || vSet?.set_code || vSet?.code || '').toLowerCase().trim();
      const vName = String(v.set_name || v.setName || vSet?.name || vSet?.set_name || vSet?.beauty_name || (typeof v.set === 'string' ? v.set : '') || '').toLowerCase().trim();
      const vVarCode = String(v.variant_code || v.variantCode || v.code || '').toLowerCase().trim();

      if (tCode) {
        if (vCode === tCode || vCode.replace(/[-_]/g, '') === tCode.replace(/[-_]/g, '')) return true;
        if (vVarCode && (vVarCode.startsWith(tCode + '-') || vVarCode.startsWith(tCode + '_') || vVarCode.includes(tCode))) return true;
      }
      if (tName) {
        if (vName === tName || vName.includes(tName) || tName.includes(vName)) return true;
      }
      return false;
    });
  }

  return false;
}

/**
 * Extrai informações completas e unificadas de Coleção / Set
 */
export function extractCardSetInfo(c: any, fallbackCode: string = '', fallbackName: string = ''): {
  setCode: string;
  setName: string;
  groupId: number | null;
  releaseDate: string | null;
} {
  if (!c) {
    return { setCode: fallbackCode, setName: fallbackName, groupId: null, releaseDate: null };
  }

  const rootSet = typeof c.set === 'object' && c.set ? c.set : null;
  const bestVariant = findMatchingVariant(c, fallbackCode, fallbackName);
  const vSet = typeof bestVariant?.set === 'object' && bestVariant?.set ? bestVariant.set : null;

  // Set Code
  const rawSetCandidates = [
    cleanSetCodeString(rootSet?.set_code),
    cleanSetCodeString(rootSet?.code),
    cleanSetCodeString(vSet?.set_code),
    cleanSetCodeString(vSet?.code),
    cleanSetCodeString(c.set_code),
    cleanSetCodeString(c.setCode),
    cleanSetCodeString(bestVariant?.set_code),
    cleanSetCodeString(bestVariant?.setCode),
    cleanSetCodeString(c.set),
    cleanSetCodeString(fallbackCode)
  ];
  const setCode = rawSetCandidates.find(Boolean) || '';

  // Set Name
  const setName = String(
    c.set_name ||
    c.setName ||
    rootSet?.name ||
    rootSet?.set_name ||
    rootSet?.beauty_name ||
    (typeof c.set === 'string' ? c.set : '') ||
    vSet?.name ||
    vSet?.set_name ||
    vSet?.beauty_name ||
    bestVariant?.set_name ||
    bestVariant?.setName ||
    fallbackName ||
    setCode ||
    ''
  ).trim();

  // Group ID / Release Date
  const groupId = rootSet?.groupId ?? vSet?.groupId ?? c.groupId ?? null;
  const releaseDate = rootSet?.release_date ?? rootSet?.releaseDate ?? vSet?.release_date ?? vSet?.releaseDate ?? c.release_date ?? c.releaseDate ?? null;

  return {
    setCode: setCode || fallbackCode,
    setName: setName || fallbackName || setCode,
    groupId: groupId ? Number(groupId) : null,
    releaseDate: releaseDate ? String(releaseDate) : null
  };
}

/**
 * Extrai e normaliza a raridade da carta
 */
export function extractCardRarityUniversal(c: any, preferredVariant?: any): string {
  if (!c) return 'Common';

  const v = preferredVariant || (Array.isArray(c.variants) && c.variants.length > 0 ? c.variants[0] : null);

  const rawRarity = c.rarity ??
                    c.Rarity ??
                    c.guardian?.rarity ??
                    c.set_rarity ??
                    c.setRarity ??
                    c.rarity_code ??
                    c.md_rarity ??
                    v?.rarity ??
                    v?.set_rarity ??
                    v?.rarity_code ??
                    (c.product_type === 'single' ? 'Common' : null);

  const parsed = parseCardRarity(rawRarity);
  if (parsed) return parsed;
  if (typeof rawRarity === 'string' && rawRarity.trim()) return rawRarity.trim();
  return 'Common';
}

/**
 * Extrai e converte os preços unitários e variantes
 */
export function extractCardPricesUniversal(c: any, preferredVariant?: any): {
  priceBrl: number;
  rawLowestUsd: number;
  marketPriceBrl: number | null;
  lowPriceBrl: number | null;
  midPriceBrl: number | null;
  highPriceBrl: number | null;
} {
  if (!c) {
    return { priceBrl: 0, rawLowestUsd: 0, marketPriceBrl: null, lowPriceBrl: null, midPriceBrl: null, highPriceBrl: null };
  }

  const validPrices: number[] = [];
  const topPrices = [c.price, c.marketPrice, c.MarketPrice, c.avgPrice];
  for (const p of topPrices) {
    if (p !== undefined && p !== null && !isNaN(Number(p)) && Number(p) > 0) {
      validPrices.push(Number(p));
    }
  }

  const variants = (Array.isArray(c.variants) && c.variants.length > 0)
    ? c.variants
    : ((Array.isArray(c.editions) && c.editions.length > 0)
      ? c.editions
      : ((Array.isArray(c.result_editions) && c.result_editions.length > 0)
        ? c.result_editions
        : ((Array.isArray(c.raw?.variants) && c.raw.variants.length > 0) ? c.raw.variants : [])));
  for (const v of variants) {
    if (!v) continue;
    const vp = v.price ?? v.marketPrice ?? v.set_price ?? v.midPrice ?? v.avgPrice ?? v.lowPrice;
    if (vp !== undefined && vp !== null && !isNaN(Number(vp)) && Number(vp) > 0) {
      validPrices.push(Number(vp));
    }
  }

  // Preço da variante preferida se informada
  let chosenUsd = 0;
  if (preferredVariant) {
    const prefP = preferredVariant.price ?? preferredVariant.marketPrice ?? preferredVariant.set_price ?? preferredVariant.midPrice ?? preferredVariant.avgPrice ?? preferredVariant.lowPrice;
    if (prefP !== undefined && prefP !== null && !isNaN(Number(prefP)) && Number(prefP) > 0) {
      chosenUsd = Number(prefP);
    }
  }

  const rawLowestUsd = chosenUsd > 0 ? chosenUsd : (validPrices.length > 0 ? Math.min(...validPrices) : 0);
  const priceBrl = Math.round(rawLowestUsd * USD_TO_BRL_RATE * 100) / 100;

  // Cotações extras da variante de referência
  const refVariant = preferredVariant || variants[0];
  const marketVal = refVariant?.marketPrice ?? c.marketPrice ?? c.market_price;
  const lowVal = refVariant?.lowPrice ?? c.lowPrice;
  const midVal = refVariant?.midPrice ?? c.midPrice;
  const highVal = refVariant?.highPrice ?? c.highPrice;

  return {
    priceBrl,
    rawLowestUsd,
    marketPriceBrl: marketVal ? Math.round(Number(marketVal) * USD_TO_BRL_RATE * 100) / 100 : null,
    lowPriceBrl: lowVal ? Math.round(Number(lowVal) * USD_TO_BRL_RATE * 100) / 100 : null,
    midPriceBrl: midVal ? Math.round(Number(midVal) * USD_TO_BRL_RATE * 100) / 100 : null,
    highPriceBrl: highVal ? Math.round(Number(highVal) * USD_TO_BRL_RATE * 100) / 100 : null
  };
}

/**
 * Normaliza e formata todas as variantes da carta
 */
export function extractCardVariantsUniversal(c: any, setInfo: { setCode: string; setName: string }): UniversalVariant[] {
  const rawVariants = Array.isArray(c.variants) && c.variants.length > 0
    ? c.variants
    : (Array.isArray(c.editions) && c.editions.length > 0
      ? c.editions
      : (Array.isArray(c.result_editions) && c.result_editions.length > 0
        ? c.result_editions
        : (Array.isArray(c.raw?.variants) && c.raw.variants.length > 0 ? c.raw.variants : [])));

  if (rawVariants.length === 0) {
    return [];
  }

  return rawVariants.map((v: any, idx: number) => {
    if (!v) return null as any;

    const vSetObj = typeof v.set === 'object' && v.set ? v.set : null;
    const vSetCode = cleanSetCodeString(vSetObj?.set_code) || 
      cleanSetCodeString(vSetObj?.code) || 
      cleanSetCodeString(vSetObj?.prefix) || 
      cleanSetCodeString(v.set_code) || 
      cleanSetCodeString(v.setCode) || 
      cleanSetCodeString(setInfo.setCode) || 
      '';
    const vSetName = v.set_name || v.setName || vSetObj?.name || vSetObj?.set_name || vSetObj?.beauty_name || setInfo.setName || '';
    const vRarity = parseCardRarity(v.rarity || v.set_rarity || v.rarity_code) || extractCardRarityUniversal(c);

    const rawVPrice = v.price ?? v.marketPrice ?? v.set_price ?? v.midPrice ?? v.avgPrice ?? v.lowPrice;
    const rawPriceNum = (rawVPrice !== undefined && rawVPrice !== null && !isNaN(Number(rawVPrice))) ? Number(rawVPrice) : 0;
    const priceBrl = Math.round(rawPriceNum * USD_TO_BRL_RATE * 100) / 100;

    const rawVImage = v.image_url || v.imageUrl || v.source_image_url || (v.images?.large || v.images?.small);
    const vImage = isUsableImageUrl(rawVImage)
      ? rawVImage
      : (extractCardImage(c) || null);

    const tcgId = v.tcgplayerId ?? v.tcgplayer_id ?? v.tcgPlayerId ?? null;

    const normalizedSetObj = vSetObj ? {
      groupId: vSetObj.groupId ?? null,
      set_code: vSetCode,
      name: vSetName,
      release_date: vSetObj.release_date ?? vSetObj.releaseDate ?? null
    } : (vSetCode || vSetName ? {
      groupId: null,
      set_code: vSetCode,
      name: vSetName,
      release_date: null
    } : null);

    const resolvedVariantCode = v.variant_code || 
      v.variantCode || 
      v.code || 
      (v.set_code && String(v.set_code).includes('-') ? String(v.set_code) : null) || 
      null;

    const resolvedVariantExtId = v.variant_external_id || 
      v.variantExternalId || 
      v.uuid || 
      v.unique_id || 
      (v.id && isNaN(Number(v.id)) ? String(v.id) : null) || 
      resolvedVariantCode || 
      null;

    return {
      id: v.id ?? v.unique_id ?? (idx + 1),
      unique_id: v.unique_id,
      tcgplayerId: tcgId,
      tcgplayer_id: tcgId,
      cardmarketId: v.cardmarketId ?? null,
      code: v.code ?? resolvedVariantCode ?? v.collector_number ?? null,
      variant_code: resolvedVariantCode,
      variant_external_id: resolvedVariantExtId,
      variant_number: v.variant_number ?? v.variantNumber ?? (idx + 1),
      collector_number: v.collector_number ?? v.print_number ?? null,
      print_number: v.print_number ?? v.collector_number ?? null,
      name: v.name ?? v.justname ?? v.juSTname ?? c.name,
      justname: v.justname ?? v.juSTname ?? null,
      printing: v.printing || v.finish || v.foiling || v.edition || null,
      finish: v.finish ?? null,
      foiling: v.foiling ?? null,
      edition: v.edition ?? null,
      condition: v.condition ?? null,
      language: v.language ?? null,
      rarity: vRarity,
      set_rarity: v.set_rarity ?? vRarity,
      rarity_code: v.rarity_code ?? null,
      set: normalizedSetObj,
      set_code: vSetCode,
      setCode: vSetCode,
      set_name: vSetName,
      setName: vSetName,
      groupId: vSetObj?.groupId ?? null,
      release_date: vSetObj?.release_date ?? vSetObj?.releaseDate ?? null,
      releaseDate: vSetObj?.releaseDate ?? vSetObj?.release_date ?? null,
      image_url: vImage ?? null,
      imageUrl: vImage ?? null,
      images: v.images ?? null,
      price: priceBrl,
      raw_price: rawPriceNum,
      marketPrice: v.marketPrice ? Math.round(Number(v.marketPrice) * USD_TO_BRL_RATE * 100) / 100 : null,
      lowPrice: v.lowPrice ? Math.round(Number(v.lowPrice) * USD_TO_BRL_RATE * 100) / 100 : null,
      midPrice: v.midPrice ? Math.round(Number(v.midPrice) * USD_TO_BRL_RATE * 100) / 100 : null,
      highPrice: v.highPrice ? Math.round(Number(v.highPrice) * USD_TO_BRL_RATE * 100) / 100 : null,
      avgPrice: v.avgPrice ? Math.round(Number(v.avgPrice) * USD_TO_BRL_RATE * 100) / 100 : null,
      raw: v
    };
  }).filter(Boolean);
}

/**
 * Função Mestra: Interpreta e devolve uma carta Homura em formato Universal pronto para uso
 */
export function parseHomuraCard(raw: any, options: ParseCardOptions = {}): UniversalCard {
  if (!raw) {
    throw new Error('parseHomuraCard: raw card payload cannot be null or undefined');
  }

  // 1. Resolução do Jogo
  const targetGameIdent = options.game || options.gameId || options.gameName || raw.game || raw.game_id || 1;
  const gameDef = findGame(targetGameIdent);
  const resolvedGameName = options.gameName || gameDef?.name || raw.game || 'TCG';
  const resolvedGameId = options.gameId || gameDef?.id || raw.game_id || 1;
  const resolvedGameSlug = gameDef?.slug || getGameSlug(targetGameIdent, 'one-piece');
  const cardBackUrl = options.cardBackUrl || (gameDef as any)?.card_back_url || (gameDef as any)?.cardBackUrl || raw.cardBackUrl || raw.card_back_url || '';

  // 2. Variante de referência & Coleção
  const bestVariant = findMatchingVariant(raw, options.fallbackSetCode, options.fallbackSetName);
  const setInfo = extractCardSetInfo(raw, options.fallbackSetCode, options.fallbackSetName);

  // 3. Identificadores principais (ID, External ID, Code)
  const v0 = bestVariant || (Array.isArray(raw.variants) && raw.variants.length > 0 ? raw.variants[0] : null);

  const rawId = String(
    raw.id ??
    raw.unique_id ??
    raw.external_id ??
    raw.code ??
    `${setInfo.setCode}_${raw.name}`
  ).trim();

  const externalId = String(
    raw.external_id ??
    raw.id ??
    raw.unique_id ??
    rawId
  ).trim();

  const rawCode = String(
    raw.code ??
    raw.card_no ??
    raw.card_num ??
    raw.number ??
    raw.print_number ??
    v0?.code ??
    v0?.variant_code ??
    v0?.collector_number ??
    raw.collector_number ??
    raw.pokemon?.number ??
    ''
  ).trim();

  // 4. Imagem
  const rawVariantImg = v0?.image_url || v0?.imageUrl || (v0?.tcgplayerId ? `https://tcgplayer-cdn.tcgplayer.com/product/${v0.tcgplayerId}_in_1000x1000.jpg` : null);
  const variantSpecificImage = isUsableImageUrl(rawVariantImg) ? rawVariantImg : null;
  const resolvedImageUrl = variantSpecificImage || extractCardImage(raw) || cardBackUrl;

  // 5. Raridade
  const resolvedRarity = extractCardRarityUniversal(raw, v0);

  // 6. Preços e Cotações
  const prices = extractCardPricesUniversal(raw, v0);

  // 7. Variantes
  const variants = extractCardVariantsUniversal(raw, setInfo);
  const vMain = (variants && variants.length > 0 ? variants[0] : null) || v0;
  const variantNumber = Number(vMain?.variant_number ?? raw.variant_number ?? 1);
  const variantCode = vMain?.variant_code || raw.variant_code || (rawCode !== rawId ? rawCode : null) || null;
  const variantExternalId = vMain?.variant_external_id || raw.variant_external_id || (vMain?.id && isNaN(Number(vMain.id)) ? String(vMain.id) : null) || externalId;

  // 8. Tipos, Classes e Categorias
  const rawTypes = Array.isArray(raw.types) ? raw.types.map(String) : [];
  const primaryType = String(
    raw.type ??
    raw.cardType ??
    raw.card_type ??
    raw.category ??
    raw.guardian?.type ??
    (rawTypes.length > 0 ? rawTypes[0] : '') ??
    ''
  ).trim();

  const subtypes = Array.isArray(raw.subtypes)
    ? raw.subtypes.map(String)
    : (typeof raw.subTypes === 'string' ? [raw.subTypes] : (raw.subtype ? [String(raw.subtype)] : []));

  const classes = Array.isArray(raw.classes)
    ? raw.classes.map(String)
    : (raw.class ? [String(raw.class)] : []);

  // 9. Cores, Elementos e Facções
  const rawColorsList: string[] = [];
  if (Array.isArray(raw.colors)) {
    raw.colors.forEach((col: any) => {
      if (typeof col === 'string') rawColorsList.push(col);
      else if (col?.name) rawColorsList.push(String(col.name));
    });
  } else if (raw.color) {
    rawColorsList.push(String(raw.color));
  } else if (raw.pokemonType) {
    rawColorsList.push(String(raw.pokemonType));
  }

  const primaryColor = rawColorsList[0] || (typeof raw.color === 'string' ? raw.color : '') || '';

  // Facção (ex: Altered)
  const factionName = typeof raw.faction === 'object' ? raw.faction?.name : (raw.faction ? String(raw.faction) : undefined);
  const factionData = typeof raw.faction === 'object' ? raw.faction : undefined;

  // Atributo (ex: One Piece, Yu-Gi-Oh)
  const attributeStr = typeof raw.attribute === 'object' ? raw.attribute?.name : (raw.attribute ? String(raw.attribute) : undefined);

  // Elemento (ex: Grand Archive, Sorcery, Altered)
  const elementStr = typeof raw.element === 'string' ? raw.element : (Array.isArray(raw.elements) ? raw.elements.join(', ') : (typeof raw.elements === 'string' ? raw.elements : undefined));

  // Tags & Traits
  const tagsList: string[] = [];
  if (Array.isArray(raw.tags)) raw.tags.forEach((t: any) => tagsList.push(String(t)));
  if (Array.isArray(raw.traits)) raw.traits.forEach((t: any) => tagsList.push(String(t)));
  if (typeof raw.trait === 'string') tagsList.push(raw.trait);
  if (raw.characterTraits) tagsList.push(String(raw.characterTraits));
  if (raw.family) tagsList.push(String(raw.family));
  if (raw.nation) tagsList.push(String(raw.nation));

  // 10. Custos & Atributos Numéricos
  const costVal = raw.cost ??
                  raw.cost_memory ??
                  raw.playCost ??
                  raw.energy ??
                  raw.elements?.MAIN_COST ??
                  raw.guardian?.cost ??
                  null;

  const powerVal = raw.power ??
                   raw.attack ??
                   raw.atk ??
                   raw.ap ??
                   raw.bp ??
                   raw.might ??
                   raw.strength ??
                   raw.threat ??
                   raw.guardian?.attack ??
                   null;

  const defenseVal = raw.defense ??
                     raw.def ??
                     raw.defence ??
                     raw.shield ??
                     raw.willpower ??
                     raw.guardian?.defence ??
                     null;

  const hpVal = raw.hp ??
                raw.life ??
                raw.vitality ??
                raw.guardian?.life ??
                null;

  // 11. Textos e Efeitos
  const descriptionText = cleanCardText(
    raw.description ||
    raw.effect ||
    raw.effect_raw ||
    raw.desc ||
    raw.rules_text ||
    raw.rulesText ||
    raw.functional_text ||
    raw.ability ||
    raw.text ||
    raw.guardian?.rulesText ||
    raw.elements?.MAIN_EFFECT ||
    raw.cd2?.raw_text ||
    raw.frontText ||
    ''
  );

  const flavorText = raw.flavor ||
                     raw.flavor_text ||
                     raw.flavorText ||
                     raw.flavor_text_plain ||
                     v0?.flavor ||
                     null;

  const illustratorName = raw.illustrator ||
                          raw.artist ||
                          raw.source_data?.illustrator ||
                          v0?.illustrator ||
                          v0?.artist ||
                          '';

  // 12. Objeto Universal Unificado
  const cardObj: UniversalCard = {
    ...raw,
    // Identidade
    id: rawId,
    external_id: externalId,
    code: rawCode || rawId,
    name: raw.name || raw.justname || raw.cleanName || 'Carta sem nome',
    cleanName: raw.cleanName || raw.name || '',
    subname: raw.subname || undefined,
    display_name: raw.display_name || (raw.subname ? `${raw.name}: ${raw.subname}` : raw.name),

    // Franquia
    game: resolvedGameName,
    game_id: resolvedGameId,
    game_slug: resolvedGameSlug,

    // Coleção / Set
    set: setInfo.setName,
    set_name: setInfo.setName,
    set_code: setInfo.setCode,
    setCode: setInfo.setCode,
    setName: setInfo.setName,
    groupId: setInfo.groupId,
    set_release_date: setInfo.releaseDate,

    // Raridade
    rarity: resolvedRarity,
    set_rarity: raw.set_rarity || resolvedRarity,
    raw_rarity: raw.rarity,
    is_foil: Boolean(raw.is_foil || v0?.foiling === 'S' || v0?.foiling === 'R' || v0?.printing === 'Foil'),

    // Imagens
    imageUrl: resolvedImageUrl,
    image_url: resolvedImageUrl,
    images: raw.images || (resolvedImageUrl ? { large: resolvedImageUrl, small: resolvedImageUrl } : undefined),
    cardBackUrl: cardBackUrl,
    card_back_url: cardBackUrl,

    // Tipos & Categorias
    type: primaryType,
    cardType: primaryType,
    card_type: primaryType,
    category: raw.category || raw.supertype || primaryType,
    stage: raw.stage || raw.pokemon?.stage || undefined,
    super: raw.super || raw.supertype || undefined,
    supertype: raw.supertype || raw.super || undefined,
    types: rawTypes.length > 0 ? rawTypes : (primaryType ? [primaryType] : []),
    subtypes: subtypes,
    classes: classes,
    class: classes.join(', ') || raw.class || undefined,
    frameType: raw.frameType || undefined,
    typeline: Array.isArray(raw.typeline) ? raw.typeline : undefined,
    classifications: raw.classifications || undefined,

    // Cores & Afiliações
    color: primaryColor,
    colors: rawColorsList,
    pokemonType: raw.pokemonType || undefined,
    attribute: attributeStr,
    faction: factionName,
    faction_data: factionData,
    element: elementStr,
    elements: raw.elements,
    nation: raw.nation || raw.clan || undefined,
    clan: raw.clan || raw.nation || undefined,
    aspects: Array.isArray(raw.aspects) ? raw.aspects : undefined,
    arenas: Array.isArray(raw.arenas) ? raw.arenas : undefined,
    domain: raw.domain || undefined,
    family: raw.family || undefined,
    trait: raw.trait || undefined,
    traits: Array.isArray(raw.traits) ? raw.traits : (raw.trait ? [raw.trait] : []),
    tags: Array.from(new Set(tagsList.filter(Boolean))),
    race: raw.race || undefined,
    archetype: raw.archetype || undefined,
    icons: Array.isArray(raw.cd3?.icons) ? raw.cd3.icons : (Array.isArray(raw.icons) ? raw.icons : undefined),

    // Atributos numéricos e de combate
    cost: costVal,
    cost_memory: raw.cost_memory ?? (typeof raw.cost === 'object' && raw.cost?.type === 'memory' ? Number(raw.cost.value) : null),
    cost_reserve: raw.cost_reserve ?? (typeof raw.cost === 'object' && raw.cost?.type === 'reserve' ? Number(raw.cost.value) : null),
    playCost: raw.playCost ?? raw.play_cost ?? null,
    energyCost: raw.energyCost ?? raw.energy_cost ?? null,
    cmc: raw.cmc ?? (raw.cost !== undefined && !isNaN(Number(raw.cost)) ? Number(raw.cost) : null),
    manaCost: raw.manaCost ?? raw.mana_cost ?? null,
    power: powerVal,
    attack: powerVal,
    defense: defenseVal,
    def: raw.def ?? defenseVal,
    toughness: raw.toughness ?? defenseVal,
    might: raw.might ?? powerVal,
    hp: hpVal,
    life: raw.life ?? hpVal,
    ap: raw.ap ?? null,
    bp: raw.bp ?? null,
    dp: raw.dp ?? powerVal,
    ram: raw.ram ?? null,
    level: raw.level ?? null,
    grade: raw.grade ?? null,
    counter: raw.counter ?? null,
    counterPower: raw.counterPower ?? null,
    comboPower: raw.comboPower ?? null,
    threat: raw.threat ?? null,
    shield: raw.shield ?? null,
    vitality: raw.vitality ?? raw.cd3?.other?.Vitality ?? null,
    hand_size: raw.hand_size ?? raw.cd3?.other?.['Hand size'] ?? null,
    difficulty: raw.difficulty ?? raw.cd3?.difficulty ?? null,
    control: raw.control ?? raw.cd3?.control ?? null,
    block: raw.block ?? raw.cd3?.block ?? null,
    block_zone: raw.block_zone ?? raw.cd3?.block_zone ?? null,
    pitch: raw.pitch ?? null,
    arcane: raw.arcane ?? null,
    lore: raw.lore ?? null,
    inkable: raw.inkable ?? null,
    strength: raw.strength ?? null,
    willpower: raw.willpower ?? null,
    bloomLevel: raw.bloomLevel || raw.bloom_level || null,
    retreat: raw.retreat ?? raw.retreat_cost ?? raw.retreatCost ?? raw.pokemon?.retreat_cost ?? null,
    weaknesses: raw.weaknesses || raw.weakness || raw.pokemon?.weakness || null,
    weakness: raw.weaknesses || raw.weakness || raw.pokemon?.weakness || null,
    resistances: raw.resistances || raw.resistance || raw.pokemon?.resistance || null,
    resistance: raw.resistances || raw.resistance || raw.pokemon?.resistance || null,

    // Textos, Habilidades, Triggers & Metadados de Gameplay
    effect: raw.effect || undefined,
    securityEffect: raw.securityEffect ?? null,
    description: descriptionText,
    text: raw.text || undefined,
    ability: raw.ability || undefined,
    rules_text: raw.rules_text || raw.rulesText || undefined,
    trigger: raw.trigger ?? null,
    affinity: raw.affinity ?? null,
    characterTraits: raw.characterTraits ?? null,
    batonPass: raw.batonPass ?? null,
    arts: raw.arts ?? null,
    oshiSkill: raw.oshiSkill ?? null,
    spOshiSkill: raw.spOshiSkill ?? null,
    cd2: raw.cd2 ?? null,
    cd3: raw.cd3 ?? null,
    guardian: raw.guardian ?? null,
    flavor: flavorText,
    flavor_text: flavorText,
    illustrator: illustratorName,
    artist: illustratorName,

    // Preços
    price: prices.priceBrl,
    raw_price: prices.rawLowestUsd,
    marketPrice: prices.marketPriceBrl,
    lowPrice: prices.lowPriceBrl,
    midPrice: prices.midPriceBrl,
    highPrice: prices.highPriceBrl,
    tcgplayerId: v0 ? (v0.tcgplayerId ?? v0.tcgplayer_id ?? null) : (raw.tcgplayerId || raw.tcgplayer_id || null),
    tcgplayer_id: v0 
      ? (v0.tcgplayer_id ? String(v0.tcgplayer_id) : (v0.tcgplayerId ? String(v0.tcgplayerId) : null)) 
      : (raw.tcgplayer_id ? String(raw.tcgplayer_id) : (raw.tcgplayerId ? String(raw.tcgplayerId) : null)),

    // Variantes
    variants: variants,
    variant_number: variantNumber,
    variant_code: variantCode,
    variant_external_id: variantExternalId,

    // Dados brutos
    raw: raw
  };

  // Se preço válido calculado, alimenta silenciosamente o cache
  if (prices.priceBrl > 0 && (rawCode || rawId)) {
    try {
      setPriceInCache(resolvedGameSlug, prices.priceBrl, undefined, rawCode || rawId);
    } catch {
      // Ignora erro de cache
    }
  }

  return cardObj;
}

/**
 * Converte uma lista de cartas ou payload de API em UniversalCard[]
 */
export function parseHomuraCards(list: any, options: ParseCardOptions = {}): UniversalCard[] {
  if (!list) return [];
  const array = Array.isArray(list) ? list : (Array.isArray(list.data) ? list.data : []);
  return array.map((item: any) => parseHomuraCard(item, options));
}

/**
 * Converte a resposta completa da API Homura com metadados de paginação
 */
export function parseHomuraApiResponse(res: any, options: ParseCardOptions = {}): {
  data: UniversalCard[];
  total: number;
  totalPages: number;
  page?: number;
  limit?: number;
} {
  if (!res) {
    return { data: [], total: 0, totalPages: 0 };
  }

  const rawList = Array.isArray(res) ? res : (Array.isArray(res.data) ? res.data : []);
  const parsedCards = rawList.map((item: any) => parseHomuraCard(item, options));

  const total = typeof res.total === 'number' ? res.total : parsedCards.length;
  const limit = typeof res.limit === 'number' && res.limit > 0 ? res.limit : (parsedCards.length || 20);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return {
    data: parsedCards,
    total,
    totalPages,
    page: res.page,
    limit: res.limit
  };
}

/**
 * Adaptador universal compatível com o legado mapHomuraCardToCard
 */
export function mapHomuraCardToCard(
  c: any,
  gameName: string = 'One Piece',
  gameId: any = 1,
  cardBackUrl?: string
): UniversalCard {
  return parseHomuraCard(c, {
    gameName,
    gameId,
    cardBackUrl
  });
}

